import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import SearchableSelect from "../common/SearchableSelect";
import { Boxes, Loader2, CheckCircle, XCircle, ExternalLink } from "lucide-react";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { checkProvisioningStatus } from "@/data/automation-server";
import {
  useKubernetesRegions,
  useProvisionKubernetesServer,
} from "@/hooks/useAutomationServersQuery";

const STAGES: Record<string, string> = {
  pending: "Asking the cluster for room",
  provisioning: "Making the namespace",
  installing_software: "Installing the server",
  registering: "Waiting for it to start and introduce itself",
};

interface ProvisionKubernetesServerModalProps {
  onServerCreated?: (serverId: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  orgId?: string;
}

export function ProvisionKubernetesServerModal({
  onServerCreated,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  orgId,
}: ProvisionKubernetesServerModalProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpenState = controlledOnOpenChange || setInternalOpen;

  const [step, setStep] = useState<"configure" | "provisioning" | "complete">("configure");
  const [serverName, setServerName] = useState("");
  const [region, setRegion] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [automationServerId, setAutomationServerId] = useState<string | null>(null);
  const [namespace, setNamespace] = useState<string | null>(null);
  const [domain, setDomain] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [provisioningError, setProvisioningError] = useState<string | null>(null);
  const [stage, setStage] = useState<string>("pending");
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const { data: regions, isLoading: regionsLoading } = useKubernetesRegions(orgId);
  const { mutate: provision, isPending: isProvisioning } = useProvisionKubernetesServer(orgId);

  const chosen = regions?.find((candidate) => candidate.slug === region);

  useEffect(() => {
    if (!region && regions && regions.length > 0) setRegion(regions[0].slug);
  }, [regions, region]);

  const handleOpenChange = (next: boolean) => {
    setOpenState(next);
    if (!next) {
      setStep("configure");
      setServerName("");
      setRegion("");
      setError(null);
      setAutomationServerId(null);
      setNamespace(null);
      setDomain(null);
      setUrl(null);
      setProvisioningError(null);
      setStage("pending");
    }
  };

  const pollStatus = useCallback(async () => {
    if (!automationServerId) return;
    try {
      const data = await checkProvisioningStatus(automationServerId, orgId);
      setProvisioningError(data.provisioning_error);
      setStage(data.provisioning_status);
      if (data.domain) setDomain(data.domain);

      if (data.provisioning_status === "completed" || data.provisioning_status === "failed") {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        if (data.provisioning_status === "completed") setStep("complete");
      }
    } catch (err) {
      console.error("Failed to check provisioning status:", err);
    }
  }, [automationServerId, orgId]);

  useEffect(() => {
    if (step === "provisioning" && automationServerId) {
      pollStatus();
      intervalRef.current = setInterval(pollStatus, 2000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [step, automationServerId, pollStatus]);

  const handleProvision = () => {
    if (!serverName.trim() || !region) {
      setError("Give the server a name and a region.");
      return;
    }
    setError(null);
    provision(
      { name: serverName.trim(), region },
      {
        onSuccess: (data) => {
          setAutomationServerId(data.automation_server_id);
          setNamespace(data.namespace);
          setDomain(data.domain);
          setUrl(data.url);
          setStep("provisioning");
        },
        onError: (err: any) => {
          setError(err.response?.data?.error || err.message || "Could not start");
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="kubernetes-server-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Boxes className="h-5 w-5 text-indigo-700" />
            Server in Kubernetes
          </DialogTitle>
          <DialogDescription>
            The server gets a namespace of its own in the region you pick. Nothing is rented and
            nothing else in the cluster can see into it.
          </DialogDescription>
        </DialogHeader>

        {step === "configure" && (
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="k8s-server-name">Name</Label>
              <Input
                id="k8s-server-name"
                value={serverName}
                placeholder="Production"
                onChange={(event) => setServerName(event.target.value)}
                data-testid="kubernetes-server-name"
              />
            </div>
            {(regions ?? []).length > 1 ? (
              <div className="grid gap-2">
                <Label>Region</Label>
                <SearchableSelect
                  options={(regions ?? []).map((candidate) => ({
                    value: candidate.slug,
                    label: candidate.name,
                  }))}
                  value={region}
                  onChange={setRegion}
                  placeholder={regionsLoading ? "Loading regions…" : "Choose a region"}
                  searchPlaceholder="Search regions…"
                  emptyText="No regions are available."
                  disabled={regionsLoading}
                  testId="kubernetes-region"
                />
              </div>
            ) : (
              <p className="text-muted-foreground text-xs" data-testid="kubernetes-region-only">
                {chosen ? `In ${chosen.name}.` : "No regions are available."}
              </p>
            )}
            {error && (
              <p className="text-destructive text-sm" data-testid="kubernetes-server-error">
                {error}
              </p>
            )}
          </div>
        )}

        {step === "provisioning" && (
          <div className="grid gap-3 py-4">
            <div className="flex items-center gap-3">
              {provisioningError ? (
                <XCircle className="h-5 w-5 text-destructive" />
              ) : (
                <Loader2 className="h-5 w-5 animate-spin" />
              )}
              <span className="text-sm">
                {provisioningError ? "The cluster refused" : STAGES[stage] ?? "Working"}
              </span>
            </div>
            {namespace && (
              <p className="text-muted-foreground font-mono text-xs">{namespace}</p>
            )}
            {provisioningError && (
              <p className="text-destructive text-sm" data-testid="kubernetes-provisioning-error">
                {provisioningError}
              </p>
            )}
          </div>
        )}

        {step === "complete" && (
          <div className="grid gap-3 py-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium">{serverName} is installed</span>
            </div>
            <p className="text-muted-foreground text-sm">
              It is running, it has introduced itself to this AOC, and the address below already
              answers.
            </p>
            {url ? (
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-xs underline"
                data-testid="kubernetes-server-url"
              >
                {url}
              </a>
            ) : null}
          </div>
        )}

        <DialogFooter>
          {step === "configure" ? (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleProvision}
                disabled={isProvisioning || !serverName.trim() || !region}
                data-testid="kubernetes-server-create"
              >
                {isProvisioning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Create
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  const created = automationServerId;
                  handleOpenChange(false);
                  if (step === "complete" && onServerCreated && created) {
                    onServerCreated(created);
                  }
                }}
              >
                {step === "complete" ? "Done" : "Close"}
              </Button>
              {step === "complete" && url ? (
                <Button asChild data-testid="kubernetes-server-open">
                  <a href={url} target="_blank" rel="noreferrer">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Open {serverName}
                  </a>
                </Button>
              ) : null}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
